<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm border-0 border-radius-xl">
          <div class="card-header pb-0 bg-transparent">
            <h6 class="font-weight-bolder">Master User Management (Admin Only)</h6>
          </div>
          
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">User Details</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Current Role</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Contact</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Use template v-for to allow two rows per user (Main + Expanded) -->
                  <template v-for="user in users" :key="'user-' + user.id">
                    <!-- Main Record Row -->
                    <tr :class="{'bg-light-selected': expandedUserId === user.id}">
                      <td class="ps-4">
                        <div class="d-flex px-2 py-1 align-items-center">
                          <!-- Avatar with user initial -->
                          <div class="avatar avatar-sm me-3 bg-gradient-dark border-radius-md d-flex align-items-center justify-content-center">
                            <span class="text-white text-xs font-weight-bold">{{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}</span>
                          </div>
                          <div class="d-flex flex-column justify-content-center">
                            <h6 class="mb-0 text-sm">{{ user.name }}</h6>
                            <p class="text-xs text-secondary mb-0">{{ user.email }}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="badge badge-sm bg-gradient-info text-uppercase">
                          {{ getRoleName(user.role_id) }}
                        </span>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">{{ user.phone }}</p>
                        <p class="text-xs text-secondary mb-0">Status: {{ user.status == 1 ? 'Active' : 'Inactive' }}</p>
                      </td>
                      <td class="align-middle text-center">
                        <button class="btn btn-link text-primary mb-0" @click="viewUserRecords(user)">
                          <i class="fas fa-eye me-1"></i> VIEW
                        </button>
                        <button 
                          class="btn btn-link mb-0" 
                          :class="expandedUserId === user.id ? 'text-danger' : 'text-dark'"
                          @click="toggleExpand(user)"
                        >
                          <i :class="expandedUserId === user.id ? 'fas fa-times' : 'fas fa-user-edit'" class="me-1"></i> 
                          {{ expandedUserId === user.id ? 'CANCEL' : 'EDIT ROLE' }}
                        </button>
                      </td>
                    </tr>

                    <!-- EXPANDED SECTION: Show Roles as Checkboxes -->
                    <tr v-if="expandedUserId === user.id">
                      <td colspan="4" class="p-0 border-0 bg-light-gray">
                        <div class="px-5 py-4">
                          <h6 class="text-xs font-weight-bolder text-uppercase text-muted mb-3">
                            Assign New Role for <span class="text-dark">{{ user.name }}</span>
                          </h6>
                          
                          <!-- Checkbox Grid -->
                          <div class="row bg-white p-3 border-radius-lg shadow-sm border mx-0">
                            <div v-for="role in roles" :key="role.id" class="col-md-3 col-6 mb-2">
                              <div class="form-check custom-role-check">
                                <input 
                                  class="form-check-input" 
                                  type="checkbox" 
                                  :id="'role-check-' + user.id + '-' + role.id"
                                  :checked="tempSelectedRoleId == role.id"
                                  @change="selectOnlyOne(role.id)"
                                >
                                <label class="form-check-label text-sm fw-bold cursor-pointer" :for="'role-check-' + user.id + '-' + role.id">
                                  {{ role.name }}
                                </label>
                              </div>
                            </div>
                          </div>

                          <div class="d-flex justify-content-end mt-3">
                            <button class="btn btn-sm btn-outline-secondary mb-0 me-2" @click="expandedUserId = null">
                              Close
                            </button>
                            <button class="btn btn-sm btn-dark mb-0" @click="saveRoleChange(user.id)">
                              <i class="fas fa-save me-1"></i> SAVE CHANGES
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "UserManagement",
  data() {
    return {
      users: [],
      roles: [],
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
      expandedUserId: null,
      tempSelectedRoleId: null
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      try {
        const [usersRes, rolesRes] = await Promise.all([
          axios.get(`${this.apiBaseUrl}/v1/admin/candidate`, config),
          axios.get(`${this.apiBaseUrl}/v1/admin/roles`, config)
        ]);
        this.users = usersRes.data;
        this.roles = rolesRes.data;
      } catch (e) {
        console.error("Error fetching data", e);
      }
    },
    getRoleName(roleId) {
      const role = this.roles.find(r => r.id == roleId);
      return role ? role.name : 'Unknown';
    },
    toggleExpand(user) {
      if (this.expandedUserId === user.id) {
        this.expandedUserId = null;
        this.tempSelectedRoleId = null;
      } else {
        this.expandedUserId = user.id;
        // Pre-check the user's current role
        this.tempSelectedRoleId = user.role_id; 
      }
    },
    selectOnlyOne(roleId) {
      // Logic to ensure only one checkbox is active (simulating radio behavior)
      this.tempSelectedRoleId = roleId;
    },
    async saveRoleChange(userId) {
      const token = localStorage.getItem("token");
      try {
        /** 
         * FIX: Updated URL from 'candidate' to 'user/update' 
         * and Method from 'put' to 'post' to match backend routes in admin.js
         */
        await axios.post(`${this.apiBaseUrl}/v1/admin/user/update/${userId}`, 
          { role_id: this.tempSelectedRoleId }, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'User role has been successfully changed.',
          timer: 2000,
          showConfirmButton: false
        });

        this.expandedUserId = null;
        this.fetchData(); // Refresh list to update Current Role badge
      } catch (e) {
        console.error("Error updating role:", e);
        Swal.fire('Error', 'Failed to update role. Please try again.', 'error');
      }
    },
    viewUserRecords(user) {
      Swal.fire({
        title: `<strong>User Profile: ${user.name}</strong>`,
        icon: 'info',
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p><strong>Name:</strong> ${user.name}</p>
            <p><strong>Email:</strong> ${user.email}</p>
            <p><strong>Phone:</strong> ${user.phone}</p>
            <p><strong>Status:</strong> ${user.status == 1 ? 'Active' : 'Inactive'}</p>
            <p><strong>Assigned Role:</strong> ${this.getRoleName(user.role_id)}</p>
          </div>
        `,
        confirmButtonColor: '#1e3a5f'
      });
    }
  }
};
</script>

<style scoped>
/* Styling for the expanded area background */
.bg-light-gray { background-color: #f4f7f6 !important; }
.bg-light-selected { background-color: #f8f9fa; }
.cursor-pointer { cursor: pointer; }

/* Themes the checkboxes to match your project colors */
.form-check-input:checked {
  background-color: #1e3a5f !important;
  border-color: #1e3a5f !important;
}

.custom-role-check {
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.custom-role-check:hover {
  background-color: #f8f9fa;
}

/* Avatar styling */
.avatar-sm {
  width: 36px;
  height: 36px;
}

.badge { font-size: 0.65rem; padding: 0.5em 0.8em; }
.btn-link { font-weight: 700; font-size: 0.75rem; }
</style>